/**
 * Helpers for `question.actions` (assessment scoring), aligned with
 * sample definitions under containers/content/questions/*.json
 *
 * Flat choice blocks (radio, checkbox, conversation, numericalscale, …):
 *   actions[blockActionId] = { values: { [key]: [ { score, component, ... } ] }, order?: [...] }
 *
 * Yes/no list (nested):
 *   actions[blockActionId][subQuestionActionId] = { values: { yes: [...], no: [...] } }
 *
 * Presentation interactions may use:
 *   actions[someActionId] = [ { score, component, ... } ]
 */

function clone(obj) {
    return obj == null ? obj : JSON.parse(JSON.stringify(obj));
}

/** Parses score from inputs; supports negative values (avoid `n || 0`). */
export function parseScoreInput(score) {
    if (score === '' || score === null || score === undefined) {
        return 0;
    }
    const n = Number(score);
    return Number.isFinite(n) ? n : 0;
}

export function getOptionActionKey(option, index) {
    if (option != null && typeof option === 'object' && !Array.isArray(option)) {
        if (option.value != null && String(option.value) !== '') {
            return String(option.value);
        }
    }
    if (typeof option === 'string') return option;
    return `__idx_${index}`;
}

export function defaultActionComponent(question) {
    const raw = question?.components || 'behaviour';
    const first = String(raw).split(',')[0]?.trim().toLowerCase() || 'behaviour';
    return first || 'behaviour';
}

function normalizeComponentToken(c) {
    const s = String(c ?? '')
        .trim()
        .toLowerCase();
    return s || 'behaviour';
}

export function getBlockOptionComponent(
    actions,
    actionId,
    optionValue,
    question
) {
    const row = actions?.[actionId]?.values?.[optionValue]?.[0];
    if (row?.component != null && String(row.component) !== '') {
        return normalizeComponentToken(row.component);
    }
    return defaultActionComponent(question);
}

export function setBlockOptionComponent(
    actions,
    actionId,
    optionValue,
    component,
    question
) {
    const a = clone(actions) || {};
    const comp = normalizeComponentToken(component);
    const cur = a[actionId];
    const base =
        cur && typeof cur === 'object' && !Array.isArray(cur) ? cur : {};
    if (!a[actionId] || typeof a[actionId] !== 'object' || Array.isArray(a[actionId])) {
        a[actionId] = { ...base };
    }
    if (!a[actionId].values) a[actionId].values = {};
    const prev = a[actionId].values[optionValue]?.[0] || {};
    const fallback = defaultActionComponent(question);
    a[actionId] = {
        ...base,
        values: {
            ...a[actionId].values,
            [optionValue]: [
                {
                    ...prev,
                    score:
                        prev.score != null && prev.score !== ''
                            ? parseScoreInput(prev.score)
                            : 0,
                    component: comp || fallback,
                },
            ],
        },
    };
    return a;
}

export function getYesNoComponent(
    actions,
    blockActionId,
    subActionId,
    yn,
    question
) {
    const row =
        actions?.[blockActionId]?.[subActionId]?.values?.[yn]?.[0];
    if (row?.component != null && String(row.component) !== '') {
        return normalizeComponentToken(row.component);
    }
    return defaultActionComponent(question);
}

/**
 * Read a single component for the yes/no row (prefers “yes” when both exist).
 * The editor shows one control but the saved JSON may still store both.
 */
export function getYesNoSharedScoringComponent(
    actions,
    blockActionId,
    subActionId,
    question
) {
    return getYesNoComponent(
        actions,
        blockActionId,
        subActionId,
        'yes',
        question
    );
}

/** Set the same `component` on both yes and no rows (scores unchanged). */
export function setYesNoSharedComponent(
    actions,
    blockActionId,
    subActionId,
    component,
    question
) {
    let a = setYesNoComponent(
        actions,
        blockActionId,
        subActionId,
        'yes',
        component,
        question
    );
    a = setYesNoComponent(
        a,
        blockActionId,
        subActionId,
        'no',
        component,
        question
    );
    return a;
}

export function setYesNoComponent(
    actions,
    blockActionId,
    subActionId,
    yn,
    component,
    question
) {
    const a = clone(actions) || {};
    const comp = normalizeComponentToken(component);
    if (!a[blockActionId]) a[blockActionId] = {};
    if (!a[blockActionId][subActionId]) {
        a[blockActionId][subActionId] = { values: {} };
    }
    const inner = a[blockActionId][subActionId];
    if (!inner.values) inner.values = {};
    const prev = inner.values[yn]?.[0] || {};
    const fallback = defaultActionComponent(question);
    inner.values[yn] = [
        {
            ...prev,
            score:
                prev.score != null && prev.score !== ''
                    ? parseScoreInput(prev.score)
                    : yn === 'yes'
                      ? 100
                      : 0,
            component: comp || fallback,
        },
    ];
    return a;
}

export function getArrayActionComponent(actions, actionId, question) {
    const row = Array.isArray(actions?.[actionId])
        ? actions[actionId][0]
        : null;
    if (row?.component != null && String(row.component) !== '') {
        return normalizeComponentToken(row.component);
    }
    return defaultActionComponent(question);
}

export function setArrayActionComponent(
    actions,
    actionId,
    component,
    question
) {
    const a = clone(actions) || {};
    const comp = normalizeComponentToken(component);
    const prev = Array.isArray(a[actionId]) ? a[actionId][0] : {};
    const fallback = defaultActionComponent(question);
    a[actionId] = [
        {
            ...prev,
            score:
                prev.score != null && prev.score !== ''
                    ? parseScoreInput(prev.score)
                    : 0,
            component: comp || fallback,
        },
    ];
    return a;
}

/** Which block field holds list entries used for flat `actions[id].values` keys. */
export function getScoringFieldAndList(block) {
    if (!block) return null;
    // Orderable list uses `options` in saved definitions (see content/questions/*.json).
    if (block.type === 'orderablelist') {
        if (Array.isArray(block.options) && block.options.length) {
            return { field: 'options', list: block.options };
        }
        if (Array.isArray(block.items) && block.items.length) {
            return { field: 'items', list: block.items };
        }
    }
    if (Array.isArray(block.options) && block.options.length) {
        return { field: 'options', list: block.options };
    }
    if (block.type === 'questionwithbuttons') {
        if (Array.isArray(block.buttons) && block.buttons.length) {
            return { field: 'buttons', list: block.buttons };
        }
    }
    return null;
}

export function getScoreForBlockOption(actions, actionId, optionValue) {
    const entry = actions?.[actionId];
    if (!entry?.values?.[optionValue]?.[0]) return 0;
    const n = Number(entry.values[optionValue][0].score);
    return Number.isFinite(n) ? n : 0;
}

export function setBlockValuesScore(
    actions,
    actionId,
    optionValue,
    score,
    question
) {
    const a = clone(actions) || {};
    const comp = defaultActionComponent(question);
    const cur = a[actionId];
    const base =
        cur && typeof cur === 'object' && !Array.isArray(cur) ? cur : {};
    if (!a[actionId] || typeof a[actionId] !== 'object' || Array.isArray(a[actionId])) {
        a[actionId] = { ...base };
    }
    if (!a[actionId].values) a[actionId].values = {};
    const prev = a[actionId].values[optionValue]?.[0] || {};
    a[actionId] = {
        ...base,
        values: {
            ...a[actionId].values,
            [optionValue]: [
                {
                    ...prev,
                    score: parseScoreInput(score),
                    component: prev.component || comp,
                },
            ],
        },
    };
    return a;
}

export function renameValueKeyInBlockAction(actions, actionId, oldKey, newKey) {
    if (!oldKey || !newKey || oldKey === newKey) return actions || {};
    const a = clone(actions) || {};
    const entry = a[actionId];
    if (!entry?.values?.[oldKey]) return actions || {};
    const { [oldKey]: payload, ...rest } = entry.values;
    a[actionId] = { ...entry, values: { ...rest, [newKey]: payload } };
    return a;
}

export function removeBlockOptionScore(actions, actionId, optionValue) {
    const a = clone(actions) || {};
    const entry = a[actionId];
    if (!entry?.values || !(optionValue in entry.values)) return a;
    const { [optionValue]: _, ...rest } = entry.values;
    a[actionId] = { ...entry, values: rest };
    return a;
}

export function ensureScoresForOptions(actions, actionId, options, question) {
    const a = clone(actions) || {};
    const prevEntry =
        a[actionId] &&
        typeof a[actionId] === 'object' &&
        !Array.isArray(a[actionId])
            ? a[actionId]
            : {};
    if (!a[actionId] || typeof a[actionId] !== 'object' || Array.isArray(a[actionId])) {
        a[actionId] = { ...prevEntry };
    }
    if (!a[actionId].values) a[actionId].values = {};
    const comp = defaultActionComponent(question);
    (options || []).forEach((opt, i) => {
        const key = getOptionActionKey(opt, i);
        if (key.startsWith('__idx_')) return;
        const row = a[actionId].values[key]?.[0];
        if (!row) {
            a[actionId].values[key] = [{ score: 0, component: comp }];
        } else if (!row.component) {
            a[actionId].values[key] = [{ ...row, component: comp }];
        }
    });
    if (prevEntry.order) {
        a[actionId] = { ...a[actionId], order: prevEntry.order };
    }
    return a;
}

export function getYesNoScore(actions, blockActionId, subActionId, yn) {
    const v =
        actions?.[blockActionId]?.[subActionId]?.values?.[yn]?.[0]?.score;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
}

/**
 * Rename top-level `actions[oldKey]` → `actions[newKey]` (block `actionId` change).
 * @returns {object|null} updated actions, or `null` if `newKey` is already used.
 */
export function renameTopLevelBlockScoringAction(actions, oldKey, newKey) {
    const a = clone(actions) || {};
    const o = String(oldKey ?? '').trim();
    const n = String(newKey ?? '').trim();
    if (!n || o === n) return a;
    if (!Object.prototype.hasOwnProperty.call(a, o)) return a;
    if (
        Object.prototype.hasOwnProperty.call(a, n) &&
        n !== o
    ) {
        return null;
    }
    const payload = a[o];
    const { [o]: _, ...rest } = a;
    return { ...rest, [n]: payload };
}

/**
 * Rename nested child under `actions[parentKey][oldChild]` → `actions[parentKey][newChild]`
 * (yes-no list item id, group-of-blocks child id).
 * @returns {object|null} updated actions, or `null` if `newChild` is already used under parent.
 */
export function renameNestedChildScoringAction(
    actions,
    parentKey,
    oldChild,
    newChild
) {
    const a = clone(actions) || {};
    const p = String(parentKey ?? '').trim();
    const o = String(oldChild ?? '').trim();
    const n = String(newChild ?? '').trim();
    if (!p || !n || !o || o === n) return a;
    const parent = a[p];
    if (!parent || typeof parent !== 'object' || Array.isArray(parent)) {
        return a;
    }
    if (!Object.prototype.hasOwnProperty.call(parent, o)) return a;
    if (
        Object.prototype.hasOwnProperty.call(parent, n) &&
        n !== o
    ) {
        return null;
    }
    const payload = parent[o];
    const { [o]: _, ...rest } = parent;
    a[p] = { ...rest, [n]: payload };
    return a;
}

/** Remove nested yes/no sub-action: actions[blockActionId][subActionId] */
export function removeYesNoSubAction(actions, blockActionId, subActionId) {
    if (!subActionId) return actions || {};
    const a = clone(actions) || {};
    const parent = a[blockActionId];
    if (!parent || typeof parent !== 'object' || Array.isArray(parent)) {
        return a;
    }
    if (!(subActionId in parent)) return a;
    const { [subActionId]: _, ...rest } = parent;
    a[blockActionId] = { ...rest };
    return a;
}

export function setYesNoScore(
    actions,
    blockActionId,
    subActionId,
    yn,
    score,
    question
) {
    const a = clone(actions) || {};
    const comp = defaultActionComponent(question);
    if (!a[blockActionId]) a[blockActionId] = {};
    if (!a[blockActionId][subActionId]) {
        a[blockActionId][subActionId] = { values: {} };
    }
    const inner = a[blockActionId][subActionId];
    if (!inner.values) inner.values = {};
    const prev = inner.values[yn]?.[0] || {};
    inner.values[yn] = [
        {
            ...prev,
            score: parseScoreInput(score),
            component: prev.component || comp,
        },
    ];
    return a;
}

export function ensureYesNoListActions(actions, block, question) {
    let a = clone(actions) || {};
    const mainId = block.actionId;
    if (!mainId) return a;
    if (!a[mainId] || typeof a[mainId] !== 'object' || Array.isArray(a[mainId])) {
        a[mainId] = {};
    }
    const comp = defaultActionComponent(question);
    (block.questions || []).forEach((q) => {
        const subId = q.actionId;
        if (!subId) return;
        if (!a[mainId][subId]) {
            a[mainId][subId] = {
                values: {
                    no: [{ score: 0, component: comp }],
                    yes: [{ score: 100, component: comp }],
                },
            };
            return;
        }
        const inner = a[mainId][subId];
        if (!inner.values) inner.values = {};
        ['yes', 'no'].forEach((yn) => {
            const row = inner.values[yn]?.[0];
            if (!inner.values[yn]?.length) {
                inner.values[yn] = [
                    {
                        score: yn === 'yes' ? 100 : 0,
                        component: comp,
                    },
                ];
            } else if (row && !row.component) {
                inner.values[yn] = [{ ...row, component: comp }];
            }
        });
    });
    return a;
}

/** Flat `actions[actionId].values` keys for password-tester (see content/questions/21.json). */
export const PASSWORD_TESTER_BAND_KEYS = ['bad', 'almost', 'perfect'];

const PASSWORD_TESTER_DEFAULT_SCORE_BY_KEY = {
    bad: 0,
    almost: 50,
    perfect: 100,
};

/**
 * Ensure `bad` / `almost` / `perfect` rows exist under `actions[actionId].values`.
 */
export function ensurePasswordTesterActions(actions, actionId, question) {
    const a = clone(actions) || {};
    if (!actionId) return a;
    const comp = defaultActionComponent(question);
    if (
        !a[actionId] ||
        typeof a[actionId] !== 'object' ||
        Array.isArray(a[actionId])
    ) {
        a[actionId] = {};
    }
    if (!a[actionId].values) a[actionId].values = {};
    PASSWORD_TESTER_BAND_KEYS.forEach((key) => {
        const row = a[actionId].values[key]?.[0];
        const defaultScore = PASSWORD_TESTER_DEFAULT_SCORE_BY_KEY[key];
        if (!row) {
            a[actionId].values[key] = [{ score: defaultScore, component: comp }];
        } else {
            const next = { ...row };
            if (next.score == null || next.score === '') {
                next.score = defaultScore;
            } else {
                next.score = parseScoreInput(next.score);
            }
            if (next.component == null || String(next.component) === '') {
                next.component = comp;
            } else {
                next.component = normalizeComponentToken(next.component);
            }
            a[actionId].values[key] = [next];
        }
    });
    return a;
}

/**
 * Decision block: `actions[blockActionId].decisions[stepId].values[valueKey]`.
 * @see content/questions/40.json
 */
export function getDecisionOptionScore(
    actions,
    blockActionId,
    stepId,
    optionValue
) {
    const row =
        actions?.[blockActionId]?.decisions?.[stepId]?.values?.[optionValue]?.[0];
    const n = Number(row?.score);
    return Number.isFinite(n) ? n : 0;
}

export function getDecisionOptionComponent(
    actions,
    blockActionId,
    stepId,
    optionValue,
    question
) {
    const row =
        actions?.[blockActionId]?.decisions?.[stepId]?.values?.[optionValue]?.[0];
    if (row?.component != null && String(row.component) !== '') {
        return normalizeComponentToken(row.component);
    }
    return defaultActionComponent(question);
}

export function setDecisionOptionScore(
    actions,
    blockActionId,
    stepId,
    optionValue,
    score,
    question
) {
    const a = clone(actions) || {};
    const comp = defaultActionComponent(question);
    if (
        !a[blockActionId] ||
        typeof a[blockActionId] !== 'object' ||
        Array.isArray(a[blockActionId])
    ) {
        a[blockActionId] = {};
    }
    if (!a[blockActionId].decisions) a[blockActionId].decisions = {};
    if (!a[blockActionId].decisions[stepId]) {
        a[blockActionId].decisions[stepId] = { values: {} };
    }
    const stepEntry = { ...a[blockActionId].decisions[stepId] };
    if (!stepEntry.values) stepEntry.values = {};
    const prev = stepEntry.values[optionValue]?.[0] || {};
    stepEntry.values = {
        ...stepEntry.values,
        [optionValue]: [
            {
                ...prev,
                score: parseScoreInput(score),
                component: prev.component || comp,
            },
        ],
    };
    a[blockActionId] = {
        ...a[blockActionId],
        decisions: {
            ...a[blockActionId].decisions,
            [stepId]: stepEntry,
        },
    };
    return a;
}

export function setDecisionOptionComponent(
    actions,
    blockActionId,
    stepId,
    optionValue,
    component,
    question
) {
    const a = clone(actions) || {};
    const compTok = normalizeComponentToken(component);
    const fallback = defaultActionComponent(question);
    if (
        !a[blockActionId] ||
        typeof a[blockActionId] !== 'object' ||
        Array.isArray(a[blockActionId])
    ) {
        a[blockActionId] = {};
    }
    if (!a[blockActionId].decisions) a[blockActionId].decisions = {};
    if (!a[blockActionId].decisions[stepId]) {
        a[blockActionId].decisions[stepId] = { values: {} };
    }
    const stepEntry = { ...a[blockActionId].decisions[stepId] };
    if (!stepEntry.values) stepEntry.values = {};
    const prev = stepEntry.values[optionValue]?.[0] || {};
    stepEntry.values = {
        ...stepEntry.values,
        [optionValue]: [
            {
                ...prev,
                score:
                    prev.score != null && prev.score !== ''
                        ? parseScoreInput(prev.score)
                        : 0,
                component: compTok || fallback,
            },
        ],
    };
    a[blockActionId] = {
        ...a[blockActionId],
        decisions: {
            ...a[blockActionId].decisions,
            [stepId]: stepEntry,
        },
    };
    return a;
}

/** Optional `correct: true` on a decision option row (40.json). */
export function setDecisionOptionCorrect(
    actions,
    blockActionId,
    stepId,
    optionValue,
    correct,
    question
) {
    const a = clone(actions) || {};
    const comp = defaultActionComponent(question);
    if (
        !a[blockActionId] ||
        typeof a[blockActionId] !== 'object' ||
        Array.isArray(a[blockActionId])
    ) {
        a[blockActionId] = {};
    }
    if (!a[blockActionId].decisions) a[blockActionId].decisions = {};
    if (!a[blockActionId].decisions[stepId]) {
        a[blockActionId].decisions[stepId] = { values: {} };
    }
    const stepEntry = { ...a[blockActionId].decisions[stepId] };
    if (!stepEntry.values) stepEntry.values = {};
    const prev = stepEntry.values[optionValue]?.[0] || {};
    stepEntry.values = {
        ...stepEntry.values,
        [optionValue]: [
            {
                ...prev,
                score:
                    prev.score != null && prev.score !== ''
                        ? parseScoreInput(prev.score)
                        : 0,
                component: prev.component || comp,
                correct: Boolean(correct),
            },
        ],
    };
    a[blockActionId] = {
        ...a[blockActionId],
        decisions: {
            ...a[blockActionId].decisions,
            [stepId]: stepEntry,
        },
    };
    return a;
}

export function removeDecisionStepActions(actions, blockActionId, stepId) {
    const a = clone(actions) || {};
    const dec = a[blockActionId]?.decisions;
    if (!dec || !(stepId in dec)) return a;
    const { [stepId]: _, ...restDec } = dec;
    return {
        ...a,
        [blockActionId]: {
            ...a[blockActionId],
            decisions: restDec,
        },
    };
}

export function ensureDecisionBlockActions(actions, block, question) {
    if (!block || block.type !== 'decision' || !block.actionId) {
        return actions || {};
    }
    let a = clone(actions) || {};
    const blockActionId = block.actionId;
    if (
        !a[blockActionId] ||
        typeof a[blockActionId] !== 'object' ||
        Array.isArray(a[blockActionId])
    ) {
        a[blockActionId] = {};
    }
    if (!a[blockActionId].decisions) a[blockActionId].decisions = {};
    const comp = defaultActionComponent(question);
    const decisions = block.decisions || {};
    Object.keys(decisions).forEach((stepId) => {
        const step = decisions[stepId];
        if (!a[blockActionId].decisions[stepId]) {
            a[blockActionId].decisions[stepId] = { values: {} };
        }
        if (!a[blockActionId].decisions[stepId].values) {
            a[blockActionId].decisions[stepId].values = {};
        }
        (step.options || []).forEach((opt, i) => {
            const key = getOptionActionKey(opt, i);
            if (key.startsWith('__idx_')) return;
            const row =
                a[blockActionId].decisions[stepId].values[key]?.[0];
            if (!row) {
                a[blockActionId].decisions[stepId].values[key] = [
                    { score: 0, component: comp },
                ];
            } else if (!row.component) {
                a[blockActionId].decisions[stepId].values[key] = [
                    { ...row, component: comp },
                ];
            }
        });
    });
    return a;
}

/** Group-of-blocks: `actions[parentId][childActionId].values` (same nesting as yes-no-list parent). */
export function getGroupChildOptionScore(
    actions,
    parentActionId,
    childActionId,
    optionValue
) {
    return getScoreForBlockOption(
        actions?.[parentActionId],
        childActionId,
        optionValue
    );
}

export function getGroupChildOptionComponent(
    actions,
    parentActionId,
    childActionId,
    optionValue,
    question
) {
    return getBlockOptionComponent(
        actions?.[parentActionId],
        childActionId,
        optionValue,
        question
    );
}

export function setGroupChildValuesScore(
    actions,
    parentActionId,
    childActionId,
    optionValue,
    score,
    question
) {
    const a = clone(actions) || {};
    const parentEntry =
        a[parentActionId] &&
        typeof a[parentActionId] === 'object' &&
        !Array.isArray(a[parentActionId])
            ? { ...a[parentActionId] }
            : {};
    const nested = setBlockValuesScore(
        parentEntry,
        childActionId,
        optionValue,
        score,
        question
    );
    return { ...a, [parentActionId]: nested };
}

export function setGroupChildOptionComponent(
    actions,
    parentActionId,
    childActionId,
    optionValue,
    component,
    question
) {
    const a = clone(actions) || {};
    const parentEntry =
        a[parentActionId] &&
        typeof a[parentActionId] === 'object' &&
        !Array.isArray(a[parentActionId])
            ? { ...a[parentActionId] }
            : {};
    const nested = setBlockOptionComponent(
        parentEntry,
        childActionId,
        optionValue,
        component,
        question
    );
    return { ...a, [parentActionId]: nested };
}

export function removeGroupChildAction(actions, parentActionId, childActionId) {
    const a = clone(actions) || {};
    const parent = a[parentActionId];
    if (!parent || typeof parent !== 'object' || Array.isArray(parent)) {
        return a;
    }
    if (!(childActionId in parent)) return a;
    const { [childActionId]: _, ...rest } = parent;
    return { ...a, [parentActionId]: rest };
}

export function ensureGroupOfBlocksActions(actions, block, question) {
    if (!block || block.type !== 'groupOfBlocks' || !block.actionId) {
        return actions || {};
    }
    let a = clone(actions) || {};
    const parentId = block.actionId;
    if (!a[parentId] || typeof a[parentId] !== 'object' || Array.isArray(a[parentId])) {
        a[parentId] = {};
    }
    const comp = defaultActionComponent(question);
    const group = block.group || block.blocks || [];
    const scorableChild = new Set([
        'radio',
        'checkbox',
        'dropDown',
        'numericalscale',
        'questionwithbuttons',
    ]);
    group.forEach((child) => {
        const childId = child.actionId;
        if (!childId || !scorableChild.has(child.type)) return;
        if (
            !a[parentId][childId] ||
            typeof a[parentId][childId] !== 'object' ||
            Array.isArray(a[parentId][childId])
        ) {
            a[parentId][childId] = { values: {} };
        }
        if (!a[parentId][childId].values) a[parentId][childId].values = {};
        const options = child.options || child.buttons || [];
        options.forEach((opt, i) => {
            const key = getOptionActionKey(opt, i);
            if (key.startsWith('__idx_')) return;
            const row = a[parentId][childId].values[key]?.[0];
            if (!row) {
                a[parentId][childId].values[key] = [{ score: 0, component: comp }];
            } else if (!row.component) {
                a[parentId][childId].values[key] = [{ ...row, component: comp }];
            }
        });
    });
    return a;
}

export function collectPresentationActionIds(presentation) {
    const out = new Set();
    const walk = (o) => {
        if (o == null) return;
        if (typeof o !== 'object') return;
        if (Array.isArray(o)) {
            o.forEach(walk);
            return;
        }
        for (const [k, v] of Object.entries(o)) {
            if (
                (k.endsWith('ActionId') ||
                    k === 'clickActionId' ||
                    k === 'hoverActionId' ||
                    k === 'actionId') &&
                typeof v === 'string' &&
                v.length > 0
            ) {
                out.add(v);
            }
            walk(v);
        }
    };
    walk(presentation);
    return [...out];
}

export function isArrayActionEntry(actions, actionId) {
    return Array.isArray(actions?.[actionId]);
}

export function getArrayActionScore(actions, actionId) {
    const arr = actions?.[actionId];
    if (!Array.isArray(arr) || !arr[0]) return 0;
    const n = Number(arr[0].score);
    return Number.isFinite(n) ? n : 0;
}

export function setArrayActionScore(actions, actionId, score, question) {
    const a = clone(actions) || {};
    const comp = defaultActionComponent(question);
    const prev = Array.isArray(a[actionId]) ? a[actionId][0] : {};
    a[actionId] = [
        {
            ...prev,
            score: parseScoreInput(score),
            component: prev.component || comp,
        },
    ];
    return a;
}
